/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 共有 AppShell の Header に渡す右側アクション群。テーマ切り替えトグルと
 *  言語切り替えメニュー（ja/en/vi）を提供する。PIL 固有の i18n 依存をここに閉じる
 * @graph-connects none
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme.js';

/** @graph-connects none */
const LANGUAGES = [
  { code: 'ja', label: '日本語' },
  { code: 'en', label: 'English' },
  { code: 'vi', label: 'Tiếng Việt' },
] as const;

/**
 * ヘッダー右側のアクション群（テーマトグル + 言語メニュー）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business テーマ切替と言語切替を提供するヘッダーアクション
 * @graph-connects none
 */
export function HeaderActions(): ReactNode {
  const { t, i18n } = useTranslation();
  const { resolved, setTheme } = useTheme();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);

  const toggleTheme = (): void => {
    setTheme(resolved === 'dark' ? 'light' : 'dark');
  };

  const currentLangCode = i18n.language.split('-')[0];
  const currentLang = LANGUAGES.find((l) => l.code === currentLangCode) ?? LANGUAGES[0];

  const handleLanguageChange = (langCode: string): void => {
    void i18n.changeLanguage(langCode);
    setIsLangMenuOpen(false);
  };

  return (
    <>
      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
        aria-label={t('settings.theme.title')}
      >
        {resolved === 'dark' ? (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        )}
      </button>

      {/* Language menu */}
      <div className="relative">
        <button
          onClick={() => {
            setIsLangMenuOpen(!isLangMenuOpen);
          }}
          className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors text-sm font-medium"
          aria-label={t('app.language')}
          aria-expanded={isLangMenuOpen}
          aria-haspopup="true"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
            />
          </svg>
          <span className="hidden sm:inline">{currentLang.label}</span>
          <svg
            className={`w-3 h-3 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isLangMenuOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => {
                setIsLangMenuOpen(false);
              }}
              aria-hidden="true"
            />
            <div className="absolute right-0 mt-2 w-40 z-20 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl shadow-xl py-1 animate-fade-in">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    handleLanguageChange(lang.code);
                  }}
                  className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                    lang.code === currentLangCode
                      ? 'text-[var(--color-accent-primary)] bg-[var(--color-accent-muted)]'
                      : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
